import { Flex, FormControl, FormHelperText, FormLabel } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import {
  useWatch,
  FieldPath,
  PathValue,
  useFormContext,
} from 'react-hook-form';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { StrictFieldValues } from './types';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';

export type FullDateStr = Util.FullDateStr;

// This ensures that this component uses the type FullDateStr | Null
type IsExactlyDateOrNull<T> = null extends T
  ? T extends Util.FullDateStr | null | undefined
    ? true
    : false
  : false;

// This type only allows react-hook-form paths that reference FullDateStr | null values.
type DatePath<T extends StrictFieldValues> = {
  [K in FieldPath<T>]: IsExactlyDateOrNull<PathValue<T, K>> extends true
    ? K
    : never;
}[FieldPath<T>];

export type DateInputProps<T extends StrictFieldValues> = {
  name: DatePath<T>;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  isDisabled?: boolean;
  tag?: React.ReactNode;
};

const fullDateStrToUTCDate = (date: Util.FullDateStr | null) => {
  if (date === null) return null;

  const [year, month, day] = date.split('-');
  return new Date(
    Date.UTC(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10)),
  );
};

/**
 * A date picker input component for selecting dates in forms. Only works with fields typed as Date | null.
 *
 * @template T - The form data type that extends StrictFieldValues
 *
 * Props:
 * - `name`: Field name in the form. Must reference a field typed as Date | null
 * - `label`: Optional label displayed above the date picker
 * - `placeholder`: Optional placeholder text shown when no date is selected. Defaults to "Enter Date"
 * - `helperText`: Optional helper text displayed below the date picker
 * - `isDisabled`: Optional, disables the component
 * - `tag`: Optional, can pass a react component to render as a tag next to the input
 *
 * @example
 * // Basic date picker
 * <DateInput<FormData>
 *   name="startDate"
 *   label="Start Date"
 * />
 *
 * @example
 * // Date picker with all options
 * <DateInput<FormData>
 *   name="deadline"
 *   label="Project Deadline"
 *   placeholder="Select deadline date"
 *   helperText="Choose a date at least 2 weeks from today"
 * />
 */
export const DateInput = <T extends StrictFieldValues>({
  name,
  label,
  placeholder = 'Enter Date',
  helperText,
  isDisabled,
  tag,
}: DateInputProps<T>) => {
  const { register, control, setValue } = useFormContext<T>();
  const selectedDateStr = useWatch<T>({ control, name }) ?? null;

  useEffect(() => {
    register(name);
  }, [name, register]);

  return (
    <FormControl>
      <Flex alignItems="center">
        {label && <FormLabel fontWeight="bold">{label}</FormLabel>}
        {tag}
      </Flex>
      <SimpleDatePicker
        width="100%"
        dateFormat="MMMM dd, yyyy"
        tz="UTC"
        placeHolder={placeholder}
        readOnly={isDisabled}
        isClearable={!isDisabled}
        // Can cast to FullDateStr | null here because DatePath ensures that this field's
        // value is of type FullDateStr | null
        selectedDate={fullDateStrToUTCDate(
          selectedDateStr as Util.FullDateStr | null,
        )}
        setSelectedDate={(date: Date | null) => {
          if (date === null) {
            setValue(name, null as any);
          } else {
            const dateStr = commonDate.dateToFullDateString(date, 'UTC');
            // Can cast to any here because DatePath ensures that this field's
            // value is of type FullDateStr | null
            setValue(name, dateStr as any);
          }
        }}
      />
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};
